import type { PostHog, PostHogConfig } from '@posthog/types';
import { Plan } from '../../../../types/CompanyPaymentDto';
import type { CurrentUserDto } from '../../../../types/UserDtos';
import { getCurrentUser } from '../CurrentUser';
import { isTrackingEnabled } from '../Tracking';
import loadScript from '../loadScript';
import { sanitizePostHogProperties } from './urlSanitizer';

const POSTHOG_OPTIONS: Partial<PostHogConfig> = {
    api_host: 'https://eu.i.posthog.com',
    defaults: '2026-05-30',
    capture_pageview: 'history_change',
    // autocapture: {
    //     element_allowlist: ['a', 'button', 'select'],
    //     url_ignorelist: ['.*app/observer/.*'],
    //     capture_copied_text: false,
    // },
    autocapture: false,
    disable_session_recording: true,
    disable_surveys: true,
    loaded: onPostHogLoad,
    secure_cookie: true,
    advanced_disable_feature_flags: true,
    advanced_disable_toolbar_metrics: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    mask_personal_data_properties: true,
    sanitize_properties: sanitizePostHogProperties,
};

const POSTHOG_API_KEY = 'phc_C3gBhq7AGBfWTGAHFB2wiMTG8ZpXDdp97w6AAW6SAZGH';

function isEligibleForPostHogRollout(user: CurrentUserDto) {
    const payment = user.company.payment;
    const isProspectInTrial = Boolean(payment) && payment.plan === Plan.None && !payment.trialExpired;
    return user.company.server === 'prod-us' && isProspectInTrial;
}

type PostHogUserProperties = {
    company_size: number;
    company_plan: string;
    company_plan_id: number;
    is_admin: boolean;
    is_hr: boolean;
    is_assistant: boolean;
    has_reports: boolean;
    is_top_of_tree: boolean;
};

function buildPostHogUserProperties(user: CurrentUserDto): PostHogUserProperties {
    const hasReports = user.reports.length > 0;
    return {
        company_size: user.company.userCount,
        company_plan: Plan[user.company.payment.plan],
        company_plan_id: user.company.payment.plan,
        is_admin: user.isAdmin,
        is_hr: user.isHR,
        is_assistant: user.isAssistant,
        has_reports: hasReports,
        is_top_of_tree: !user.managerId && hasReports,
    };
}

async function onPostHogLoad(posthog: PostHog) {
    const user = getCurrentUser();
    posthog.identify(user.id, buildPostHogUserProperties(user));
    posthog.group('company', user.company.id);
}

export function initPostHog() {
    try {
        const user = getCurrentUser();
        if (!isTrackingEnabled() || !user.isHR || !isEligibleForPostHogRollout(user)) {
            return;
        }

        if (window.posthog) {
            return;
        }

        loadScript(POSTHOG_OPTIONS.api_host!.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js')
            .then(() => {
                window.posthog.init(POSTHOG_API_KEY, POSTHOG_OPTIONS);
            })
            .catch(err => {
                // eslint-disable-next-line no-console
                console.error('Error loading PostHog script:', err);
            });
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error initializing PostHog:', err);
    }
}
